import * as React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import AddButton from './AddButton';
import RemoveButton from './RemoveButton';
import { X, Tag } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Reusable tag input component for managing tags/chips (websites, skills, etc.)
 * Uses shadcn/ui components for modern design
 * @param {Array} tags - Array of tag strings
 * @param {Function} onAdd - Callback when tag is added: (tag) => void
 * @param {Function} onRemove - Callback when tag is removed: (index) => void
 * @param {string} label - Label for the input
 * @param {string} placeholder - Placeholder text
 * @param {number} maxLength - Maximum length for input
 * @param {string} className - Additional CSS classes
 */
const TagInput = React.forwardRef(({
  tags = [],
  onAdd,
  onRemove,
  label = "Tags",
  placeholder = "Enter a tag...",
  maxLength = 100,
  className = "",
}, ref) => {
  const [newTag, setNewTag] = React.useState("");

  const handleAdd = () => {
    const trimmedTag = newTag.trim();
    if (trimmedTag && onAdd) {
      onAdd(trimmedTag);
      setNewTag("");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div ref={ref} className={cn("space-y-2", className)}>
      {label && <Label>{label}</Label>}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            type="text"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            maxLength={maxLength}
            className="pl-9 placeholder:text-muted-foreground/60 placeholder:italic"
          />
        </div>
        <AddButton onClick={handleAdd} variant="secondary" size="default">
          Add
        </AddButton>
      </div>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag, idx) => (
            <Badge key={idx} variant="secondary" className="gap-2 pr-1">
              {tag}
              <RemoveButton 
                onClick={() => onRemove && onRemove(idx)} 
                ariaLabel={`Remove ${label.toLowerCase()} ${idx + 1}`}
                size="icon"
                variant="ghost"
                className="h-5 w-5 hover:bg-destructive/10 hover:text-destructive"
                useXIcon={true}
              />
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
});

TagInput.displayName = "TagInput";

export default TagInput;
